// The Moon's geocentric state from Meeus, Astronomical Algorithms (2nd ed.)
// ch. 47: ELP-2000/82 cut to 60 longitude and distance terms, 60 latitude terms
// and the nine additive A1/A2/A3 and flattening terms, stated good to 10 arcsec in longitude and 4 in
// latitude. Its ecliptic and equinox of date go to J2000 by Lieske's precession
// (Meeus eq. 21.5), and the velocity is the series' analytic derivative through
// the same rotation. Pure, so tools/preview runs it under Node.

const DEG = Math.PI / 180;
const ARCSEC = DEG / 3600;
const SECONDS_PER_CENTURY = 36525 * 86400;

/** G(M_Earth + M_Moon), DE440, km^3/s^2: the two-body parameter the Moon's osculating orbit about Earth takes. */
export const GM_EARTH_MOON_KM3_S2 = 403503.235625;

/**
 * How far the osculating ellipse through this series' state moves in a day, km,
 * bounded: it read 33,809 at worst over 440 days from the 2078 anchor, 30 times
 * what the row's mean node and perigee rates alone turn it.
 */
export const LUNAR_ELLIPSE_DRIFT_KM_PER_DAY = 40_000;

// Meeus table 47.A: multiples of D, M, M', F, then the longitude term (1e-6 deg,
// on sin) and the distance term (1e-3 km, on cos).
const LR_TERMS: readonly number[] = [
  0, 0, 1, 0, 6288774, -20905355,
  2, 0, -1, 0, 1274027, -3699111,
  2, 0, 0, 0, 658314, -2955968,
  0, 0, 2, 0, 213618, -569925,
  0, 1, 0, 0, -185116, 48888,
  0, 0, 0, 2, -114332, -3149,
  2, 0, -2, 0, 58793, 246158,
  2, -1, -1, 0, 57066, -152138,
  2, 0, 1, 0, 53322, -170733,
  2, -1, 0, 0, 45758, -204586,
  0, 1, -1, 0, -40923, -129620,
  1, 0, 0, 0, -34720, 108743,
  0, 1, 1, 0, -30383, 104755,
  2, 0, 0, -2, 15327, 10321,
  0, 0, 1, 2, -12528, 0,
  0, 0, 1, -2, 10980, 79661,
  4, 0, -1, 0, 10675, -34782,
  0, 0, 3, 0, 10034, -23210,
  4, 0, -2, 0, 8548, -21636,
  2, 1, -1, 0, -7888, 24208,
  2, 1, 0, 0, -6766, 30824,
  1, 0, -1, 0, -5163, -8379,
  1, 1, 0, 0, 4987, -16675,
  2, -1, 1, 0, 4036, -12831,
  2, 0, 2, 0, 3994, -10445,
  4, 0, 0, 0, 3861, -11650,
  2, 0, -3, 0, 3665, 14403,
  0, 1, -2, 0, -2689, -7003,
  2, 0, -1, 2, -2602, 0,
  2, -1, -2, 0, 2390, 10056,
  1, 0, 1, 0, -2348, 6322,
  2, -2, 0, 0, 2236, -9884,
  0, 1, 2, 0, -2120, 5751,
  0, 2, 0, 0, -2069, 0,
  2, -2, -1, 0, 2048, -4950,
  2, 0, 1, -2, -1773, 4130,
  2, 0, 0, 2, -1595, 0,
  4, -1, -1, 0, 1215, -3958,
  0, 0, 2, 2, -1110, 0,
  3, 0, -1, 0, -892, 3258,
  2, 1, 1, 0, -810, 2616,
  4, -1, -2, 0, 759, -1897,
  0, 2, -1, 0, -713, -2117,
  2, 2, -1, 0, -700, 2354,
  2, 1, -2, 0, 691, 0,
  2, -1, 0, -2, 596, 0,
  4, 0, 1, 0, 549, -1423,
  0, 0, 4, 0, 537, -1117,
  4, -1, 0, 0, 520, -1571,
  1, 0, -2, 0, -487, -1739,
  2, 1, 0, -2, -399, 0,
  0, 0, 2, -2, -381, -4421,
  1, 1, 1, 0, 351, 0,
  3, 0, -2, 0, -340, 0,
  4, 0, -3, 0, 330, 0,
  2, -1, 2, 0, 327, 0,
  0, 2, 1, 0, -323, 1165,
  1, 1, -1, 0, 299, 0,
  2, 0, 3, 0, 294, 0,
  2, 0, -1, -2, 0, 8752,
];

// Meeus table 47.B: multiples of D, M, M', F, then the latitude term (1e-6 deg, on sin).
const B_TERMS: readonly number[] = [
  0, 0, 0, 1, 5128122,
  0, 0, 1, 1, 280602,
  0, 0, 1, -1, 277693,
  2, 0, 0, -1, 173237,
  2, 0, -1, 1, 55413,
  2, 0, -1, -1, 46271,
  2, 0, 0, 1, 32573,
  0, 0, 2, 1, 17198,
  2, 0, 1, -1, 9266,
  0, 0, 2, -1, 8822,
  2, -1, 0, -1, 8216,
  2, 0, -2, -1, 4324,
  2, 0, 1, 1, 4200,
  2, 1, 0, -1, -3359,
  2, -1, -1, 1, 2463,
  2, -1, 0, 1, 2211,
  2, -1, -1, -1, 2065,
  0, 1, -1, -1, -1870,
  4, 0, -1, -1, 1828,
  0, 1, 0, 1, -1794,
  0, 0, 0, 3, -1749,
  0, 1, -1, 1, -1565,
  1, 0, 0, 1, -1491,
  0, 1, 1, 1, -1475,
  0, 1, 1, -1, -1410,
  0, 1, 0, -1, -1344,
  1, 0, 0, -1, -1335,
  0, 0, 3, 1, 1107,
  4, 0, 0, -1, 1021,
  4, 0, -1, 1, 833,
  0, 0, 1, -3, 777,
  4, 0, -2, 1, 671,
  2, 0, 0, -3, 607,
  2, 0, 2, -1, 596,
  2, -1, 1, -1, 491,
  2, 0, -2, 1, -451,
  0, 0, 3, -1, 439,
  2, 0, 2, 1, 422,
  2, 0, -3, -1, 421,
  2, 1, -1, 1, -366,
  2, 1, 0, 1, -351,
  4, 0, 0, 1, 331,
  2, -1, 1, 1, 315,
  2, -2, 0, -1, 302,
  0, 0, 1, 3, -283,
  2, 1, 1, -1, -229,
  1, 1, 0, -1, 223,
  1, 1, 0, 1, 223,
  0, 1, -2, -1, -220,
  2, 1, -1, -1, -220,
  1, 0, 1, 1, -185,
  2, -1, -2, -1, 181,
  0, 1, 2, 1, -177,
  4, 0, -2, -1, 176,
  4, -1, -1, -1, 166,
  1, 0, 1, -1, -164,
  4, 0, 1, -1, 132,
  1, 0, -1, -1, -119,
  4, -1, 0, -1, 115,
  2, -2, 0, 1, 107,
];

// cos and sin of 0..k times each fundamental argument, so a term's own
// argument is at most three complex products instead of its own trig calls.
const COS_D = new Float64Array(5); const SIN_D = new Float64Array(5);
const COS_M = new Float64Array(3); const SIN_M = new Float64Array(3);
const COS_MP = new Float64Array(5); const SIN_MP = new Float64Array(5);
const COS_F = new Float64Array(4); const SIN_F = new Float64Array(4);

function fillMultiples(c: Float64Array, s: Float64Array, angle: number): void {
  const ca = Math.cos(angle);
  const sa = Math.sin(angle);
  c[0] = 1;
  s[0] = 0;
  for (let k = 1; k < c.length; k++) {
    c[k] = c[k - 1]! * ca - s[k - 1]! * sa;
    s[k] = s[k - 1]! * ca + c[k - 1]! * sa;
  }
}

/** Position in km, velocity in km/s, filled in place. */
/** `ephem-check.mjs --inject no-periodic` sets `meanOnly` to read the mean longitude and mean distance alone; plain Node has no `?` switch to carry it. */
export const LUNAR_CONTROL = { meanOnly: false };

export interface LunarStateOut {
  position: { x: number; y: number; z: number };
  velocity: { x: number; y: number; z: number };
}

// One term's argument, its cos and sin and its rate, reused across terms.
let termCos = 0;
let termSin = 0;

function termArgument(d: number, m: number, mp: number, f: number): void {
  let c = COS_D[d]!;
  let s = SIN_D[d]!;
  if (m !== 0) {
    const k = m < 0 ? -m : m;
    const cc = COS_M[k]!;
    const ss = m < 0 ? -SIN_M[k]! : SIN_M[k]!;
    const t = c * cc - s * ss; s = s * cc + c * ss; c = t;
  }
  if (mp !== 0) {
    const k = mp < 0 ? -mp : mp;
    const cc = COS_MP[k]!;
    const ss = mp < 0 ? -SIN_MP[k]! : SIN_MP[k]!;
    const t = c * cc - s * ss; s = s * cc + c * ss; c = t;
  }
  if (f !== 0) {
    const k = f < 0 ? -f : f;
    const cc = COS_F[k]!;
    const ss = f < 0 ? -SIN_F[k]! : SIN_F[k]!;
    const t = c * cc - s * ss; s = s * cc + c * ss; c = t;
  }
  termCos = c;
  termSin = s;
}

/**
 * Geocentric ecliptic J2000 state of the Moon at a TDB Julian date: position
 * in km, velocity in km/s, geometric but for the 0.70 arcsec light-time constant Meeus folds into L'
 * (inside the 3.38 arcsec read against Horizons), the frame and time scale
 * every other row's `stateAt` answers in.
 */
export function lunarStateAt(jdTdb: number, out: LunarStateOut): LunarStateOut {
  const T = (jdTdb - 2451545.0) / 36525;
  const T2 = T * T;
  const T3 = T2 * T;
  const T4 = T3 * T;

  // Meeus 47.1-47.5: mean longitude, elongation, the Sun's and the Moon's mean
  // anomalies and the argument of latitude, with their rates per century.
  const L = (218.3164477 + 481267.88123421 * T - 0.0015786 * T2 + T3 / 538841 - T4 / 65194000) * DEG;
  const Ld = (481267.88123421 - 0.0031572 * T + 3 * T2 / 538841 - 4 * T3 / 65194000) * DEG;
  const D = (297.8501921 + 445267.1114034 * T - 0.0018819 * T2 + T3 / 545868 - T4 / 113065000) * DEG;
  const Dd = (445267.1114034 - 0.0037638 * T + 3 * T2 / 545868 - 4 * T3 / 113065000) * DEG;
  const M = (357.5291092 + 35999.0502909 * T - 0.0001536 * T2 + T3 / 24490000) * DEG;
  const Md = (35999.0502909 - 0.0003072 * T + 3 * T2 / 24490000) * DEG;
  const Mp = (134.9633964 + 477198.8675055 * T + 0.0087414 * T2 + T3 / 69699 - T4 / 14712000) * DEG;
  const Mpd = (477198.8675055 + 0.0174828 * T + 3 * T2 / 69699 - 4 * T3 / 14712000) * DEG;
  const F = (93.2720950 + 483202.0175233 * T - 0.0036539 * T2 - T3 / 3526000 + T4 / 863310000) * DEG;
  const Fd = (483202.0175233 - 0.0073078 * T - 3 * T2 / 3526000 + 4 * T3 / 863310000) * DEG;
  // 47.6: the Earth's orbital eccentricity factor on every term in M.
  const E = 1 - 0.002516 * T - 0.0000074 * T2;
  const Ed = -0.002516 - 0.0000148 * T;
  const E2 = E * E;
  const E2d = 2 * E * Ed;

  fillMultiples(COS_D, SIN_D, D);
  fillMultiples(COS_M, SIN_M, M);
  fillMultiples(COS_MP, SIN_MP, Mp);
  fillMultiples(COS_F, SIN_F, F);

  let sl = 0; let sld = 0;
  let sr = 0; let srd = 0;
  let sb = 0; let sbd = 0;
  for (let i = 0; i < LR_TERMS.length; i += 6) {
    const d = LR_TERMS[i]!; const m = LR_TERMS[i + 1]!; const mp = LR_TERMS[i + 2]!; const f = LR_TERMS[i + 3]!;
    termArgument(d, m, mp, f);
    const rate = d * Dd + m * Md + mp * Mpd + f * Fd;
    const e = m === 0 ? 1 : (m === 1 || m === -1) ? E : E2;
    const ed = m === 0 ? 0 : (m === 1 || m === -1) ? Ed : E2d;
    const kl = LR_TERMS[i + 4]!;
    const kr = LR_TERMS[i + 5]!;
    sl += kl * e * termSin;
    sld += kl * (ed * termSin + e * termCos * rate);
    sr += kr * e * termCos;
    srd += kr * (ed * termCos - e * termSin * rate);
  }
  for (let i = 0; i < B_TERMS.length; i += 5) {
    const d = B_TERMS[i]!; const m = B_TERMS[i + 1]!; const mp = B_TERMS[i + 2]!; const f = B_TERMS[i + 3]!;
    termArgument(d, m, mp, f);
    const rate = d * Dd + m * Md + mp * Mpd + f * Fd;
    const e = m === 0 ? 1 : (m === 1 || m === -1) ? E : E2;
    const ed = m === 0 ? 0 : (m === 1 || m === -1) ? Ed : E2d;
    const kb = B_TERMS[i + 4]!;
    sb += kb * e * termSin;
    sbd += kb * (ed * termSin + e * termCos * rate);
  }

  // A1 Venus, A2 Jupiter, A3 the Earth's flattening; the L' - F and L' +/- M'
  // terms beside them are the same corrections' own arguments.
  const A1 = (119.75 + 131.849 * T) * DEG;
  const A1d = 131.849 * DEG;
  const A2 = (53.09 + 479264.290 * T) * DEG;
  const A2d = 479264.290 * DEG;
  const A3 = (313.45 + 481266.484 * T) * DEG;
  const A3d = 481266.484 * DEG;
  sl += 3958 * Math.sin(A1) + 1962 * Math.sin(L - F) + 318 * Math.sin(A2);
  sld += 3958 * Math.cos(A1) * A1d + 1962 * Math.cos(L - F) * (Ld - Fd) + 318 * Math.cos(A2) * A2d;
  sb += -2235 * Math.sin(L) + 382 * Math.sin(A3) + 175 * Math.sin(A1 - F) + 175 * Math.sin(A1 + F)
    + 127 * Math.sin(L - Mp) - 115 * Math.sin(L + Mp);
  sbd += -2235 * Math.cos(L) * Ld + 382 * Math.cos(A3) * A3d
    + 175 * Math.cos(A1 - F) * (A1d - Fd) + 175 * Math.cos(A1 + F) * (A1d + Fd)
    + 127 * Math.cos(L - Mp) * (Ld - Mpd) - 115 * Math.cos(L + Mp) * (Ld + Mpd);

  if (LUNAR_CONTROL.meanOnly) { sl = 0; sld = 0; sr = 0; srd = 0; sb = 0; sbd = 0; }

  // Ecliptic and mean equinox of date; rates per century until the last step.
  const lon = L + sl * 1e-6 * DEG;
  const lonD = Ld + sld * 1e-6 * DEG;
  const lat = sb * 1e-6 * DEG;
  const latD = sbd * 1e-6 * DEG;
  const r = 385000.56 + sr * 1e-3;
  const rD = srd * 1e-3;
  const cLon = Math.cos(lon); const sLon = Math.sin(lon);
  const cLat = Math.cos(lat); const sLat = Math.sin(lat);
  const x = r * cLat * cLon;
  const y = r * cLat * sLon;
  const z = r * sLat;
  const xd = rD * cLat * cLon - r * sLat * latD * cLon - r * cLat * sLon * lonD;
  const yd = rD * cLat * sLon - r * sLat * latD * sLon + r * cLat * cLon * lonD;
  const zd = rD * sLat + r * cLat * latD;

  // Date back to J2000: Rz(-Pi) Rx(-eta) Rz(Pi + p), each a rotation of the axes,
  // with Lieske's eta, Pi and p taken from J2000 to the date. The rates carry the
  // frame's own turn, which is about 3 mm/s here.
  const eta = (47.0029 * T - 0.03302 * T2 + 0.000060 * T3) * ARCSEC;
  const etaD = (47.0029 - 0.06604 * T + 0.00018 * T2) * ARCSEC;
  const Pi = 174.876384 * DEG + (-869.8089 * T + 0.03536 * T2) * ARCSEC;
  const PiD = (-869.8089 + 0.07072 * T) * ARCSEC;
  const p = (5029.0966 * T + 1.11113 * T2 - 0.000006 * T3) * ARCSEC;
  const pD = (5029.0966 + 2.22226 * T - 0.000018 * T2) * ARCSEC;

  const a1 = Pi + p;
  const a1D = PiD + pD;
  const c1 = Math.cos(a1); const s1 = Math.sin(a1);
  const x1 = c1 * x + s1 * y;
  const y1 = -s1 * x + c1 * y;
  const x1d = c1 * xd + s1 * yd + a1D * y1;
  const y1d = -s1 * xd + c1 * yd - a1D * x1;
  const ce = Math.cos(eta); const se = Math.sin(eta);
  const y2 = ce * y1 - se * z;
  const z2 = se * y1 + ce * z;
  const y2d = ce * y1d - se * zd - etaD * z2;
  const z2d = se * y1d + ce * zd + etaD * y2;
  const c3 = Math.cos(Pi); const s3 = Math.sin(Pi);
  const x3 = c3 * x1 - s3 * y2;
  const y3 = s3 * x1 + c3 * y2;
  const x3d = c3 * x1d - s3 * y2d - PiD * y3;
  const y3d = s3 * x1d + c3 * y2d + PiD * x3;

  out.position.x = x3;
  out.position.y = y3;
  out.position.z = z2;
  out.velocity.x = x3d / SECONDS_PER_CENTURY;
  out.velocity.y = y3d / SECONDS_PER_CENTURY;
  out.velocity.z = z2d / SECONDS_PER_CENTURY;
  return out;
}
